/**
 * Collapsible tabs: animate open and closed.
 *
 * The theme animated block-size 0 -> auto behind `interpolate-size:
 * allow-keywords`, which only Chromium implements. On Safari - most of this
 * store's traffic - the panel jumped.
 *
 * The closing direction never ran anywhere, for a different reason: a closed
 * <details> stops rendering its content, so the element is gone before a
 * transition can start. That is the same problem the size sheet has with
 * WebKit and `overlay`, and it has the same answer - hold the element open
 * until the animation finishes, then close it.
 *
 * Progressive enhancement: without this script the CSS in
 * product-block-collapsible-tabs.liquid still opens and closes the panel, just
 * instantly. Nothing depends on JS to read the content.
 */
(() => {
  const SUMMARY = '.collapsible-tabs > details > summary';

  /**
   * Read off :root so the accordion and the size sheet share one motion
   * language - the sheet applies the same two values through CSS.
   * @returns {{duration: number, easing: string}}
   */
  const motion = () => {
    const style = getComputedStyle(document.documentElement);
    const raw = style.getPropertyValue('--pdp-motion').trim();
    const value = parseFloat(raw);
    // Shopify minifies the stylesheet, so `280ms` is served as `.28s`. Reading
    // the number alone gave a 0.28ms animation - instant, and indistinguishable
    // from the script not running at all.
    const duration = Number.isFinite(value)
      ? (/ms\s*$/.test(raw) ? value : value * 1000)
      : 280;
    return { duration, easing: style.getPropertyValue('--pdp-ease').trim() || 'ease' };
  };

  /**
   * @param {HTMLDetailsElement} details
   * @returns {HTMLElement|null}
   */
  const panelOf = (details) => details.querySelector(':scope > .rte');

  /**
   * @param {HTMLDetailsElement} details
   * @param {HTMLElement} panel
   * @returns {void}
   */
  const toggle = (details, panel) => {
    // Intent, not the element's current state: while a close is playing the
    // details is still open, so reading `open` would close it a second time
    // rather than reversing.
    const settled = details.dataset.state || (details.open ? 'open' : 'closed');
    const opening = settled !== 'open';
    details.dataset.state = opening ? 'open' : 'closed';

    // Reverse an animation still in flight rather than ignoring the tap. An
    // earlier version held a boolean lock instead, which latched forever if a
    // run never finished - backgrounding the tab mid-animation pauses the
    // timeline, `finish` never fires, and the accordion stops responding.
    //
    // Snapshot before cancelling. cancel() drops the effect in the same frame,
    // so the computed style reverts to the resting cascade - read it after and
    // the panel's real half-open position is already gone. Starting a reversal
    // from a hardcoded extreme made an interrupted tap snap shut, or jump to
    // full height, for a frame before moving the right way.
    const inFlight = panel.getAnimations();
    let resumeFrom = null;
    if (inFlight.length > 0) {
      const mid = getComputedStyle(panel);
      resumeFrom = {
        blockSize: mid.blockSize,
        marginBlockStart: mid.marginBlockStart,
        marginBlockEnd: mid.marginBlockEnd,
        opacity: mid.opacity,
      };
      for (const running of inFlight) running.cancel();
    }

    if (matchMedia('(prefers-reduced-motion: reduce)').matches) {
      details.open = opening;
      panel.style.overflow = '';
      return;
    }

    // Opening has to render the content before it can be measured.
    if (opening) details.open = true;

    // The panel's margins have to travel with its height. A closed <details>
    // does not render its content at all, so those margins vanish the instant
    // `open` flips - animating height alone ran the panel down to zero with
    // 22px of margin still under it, then dropped that in one frame at the
    // end. That was the jump.
    const style = getComputedStyle(panel);
    const shut = { blockSize: '0px', marginBlockStart: '0px', marginBlockEnd: '0px', opacity: 0 };
    const full = {
      blockSize: `${panel.scrollHeight}px`,
      marginBlockStart: style.marginBlockStart,
      marginBlockEnd: style.marginBlockEnd,
      opacity: 1,
    };

    const from = resumeFrom ?? (opening ? shut : full);
    const to = opening ? full : shut;

    panel.style.overflow = 'hidden';
    const animation = panel.animate([from, to], motion());

    let settledUp = false;
    const finish = () => {
      if (settledUp) return;
      settledUp = true;
      panel.style.overflow = '';
      // The resting state is the stylesheet's, not this animation's - so a
      // panel left open keeps block-size: auto and reflows with its content.
      if (!opening) details.open = false;
    };
    animation.addEventListener('finish', finish, { once: true });
    // A cancel means a newer toggle has taken over; it owns the resting state.
    animation.addEventListener('cancel', () => { settledUp = true; }, { once: true });
    // Ceiling, for the same reason the size sheet has one: a paused or dropped
    // timeline must not be able to strand the panel part-open.
    setTimeout(finish, motion().duration + 80);
  };

  // Delegated, so tabs rendered after load are covered too. Enter and Space on
  // a focused summary both dispatch click, so this handles keyboard as well.
  document.addEventListener('click', (event) => {
    const summary = event.target.closest?.(SUMMARY);
    if (!summary) return;
    const details = summary.parentElement;
    const panel = panelOf(details);
    if (!panel) return;

    event.preventDefault();
    toggle(details, panel);
  });
})();
